import type { GuidedSection, SectionStatusMap } from '@lds-efsp/shared-contracts';

/**
 * The guided-section half of the attached-case reconcile (SPEC-SOR-174 R18).
 *
 * When the attached case changes, the case, parties and documents sections
 * revert to `pending` and the filer's confirm act on those same three is
 * discarded — an approval granted for the replaced case is not an approval of
 * the one that replaced it. `sec_court` is deliberately untouched, in BOTH its
 * status and its provenance: the filing court did not change, so nothing about
 * the court section's approval went stale.
 *
 * This is the case-change analogue of `reset-court-scoped-case.ts`'s
 * `resetSectionStatuses`, which voids all four sections because a court change
 * invalidates everything below it. Kept pure and separate from the context for
 * the same reason that one is: the transition is exhaustively testable over
 * every status × provenance combination, including the `confirmed` states that
 * only the confirm control (a later child) can actually produce.
 */

/**
 * The sections a case change voids. `sec_court` is absent BY OMISSION rather
 * than by being singled out for preservation, so a section added to the enum
 * later survives a case change by default instead of silently disappearing
 * from a draft — the same "protected by default" discipline the extraction
 * guard's total key-ownership functions follow.
 */
export const CASE_SCOPED_SECTIONS: readonly GuidedSection[] = [
  'sec_case',
  'sec_parties',
  'sec_documents',
];

/** The two persisted guided-section fields this transition rewrites. */
export interface CaseAttachSectionState {
  sectionStatuses?: SectionStatusMap;
  filerConfirmedSections?: GuidedSection[];
}

const isCaseScoped = (section: GuidedSection): boolean =>
  CASE_SCOPED_SECTIONS.includes(section);

/**
 * Compute the post-case-change guided-section state.
 *
 * The three case-scoped sections are DROPPED from the map rather than written
 * `pending`, exactly as the court-change reset drops all four: an absent entry
 * IS `pending` to the sole reader (`getSectionStatus`), so dropping keeps one
 * representation of "nothing confirmed here" instead of two. For the same
 * reason an emptied map — and an emptied confirm-act list — collapses back to
 * `undefined` rather than `{}` / `[]`, which is the shape `defaultWizardState`
 * and every legacy draft already carry.
 */
export function revertCaseAttachSectionState(
  prev: CaseAttachSectionState,
): CaseAttachSectionState {
  const surviving: SectionStatusMap = {};
  for (const [section, status] of Object.entries(prev.sectionStatuses ?? {})) {
    if (isCaseScoped(section as GuidedSection)) continue;
    surviving[section as GuidedSection] = status;
  }

  const survivingActs = (prev.filerConfirmedSections ?? []).filter(s => !isCaseScoped(s));

  return {
    sectionStatuses: Object.keys(surviving).length > 0 ? surviving : undefined,
    filerConfirmedSections: survivingActs.length > 0 ? survivingActs : undefined,
  };
}
